/**
 * The slowly-turning ring of text that orbits the closing image.
 *
 * Set on a circular `textPath` so the letters follow the curve rather than
 * being rotated individually, and turned by a CSS animation on the group —
 * which means it holds a legible position when reduced motion is on instead of
 * needing a separate static variant.
 *
 * The ring repeats the phrase twice so the text meets itself; a single pass
 * leaves an obvious gap on the far side.
 */

interface RotatingSealProps {
  text: string;
  className?: string;
}

export function RotatingSeal({ text, className = '' }: RotatingSealProps) {
  // A unique-enough path id per phrase: the seal appears once per page, and
  // deriving it from the text keeps it stable across the prerender and the
  // client render (a random id would mismatch and break the reference).
  const pathId = `fias-seal-${text.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  const phrase = `${text} • `;

  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
      <defs>
        <path id={pathId} d="M 100,100 m -76,0 a 76,76 0 1,1 152,0 a 76,76 0 1,1 -152,0" />
      </defs>
      <g className="fias-seal">
        <text
          fill="currentColor"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            letterSpacing: '0.06em',
          }}
        >
          <textPath href={`#${pathId}`} startOffset="0">
            {phrase.repeat(2)}
          </textPath>
        </text>
      </g>
    </svg>
  );
}
